class InteractiveMap {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.wrapper = document.getElementById(sectionId + '-svg-wrapper');
    this.svg = null;
    this.originalViewBox = null;
    this.currentViewBox = null;
    this.animationId = null;
    this.currentBlockId = null;
    this.currentPointName = null;
    this.pendingFocus = null;
    this._popupSwiper = null;
    this.init();
  }

  init() {
    this.portalPopupOverlay();
    this.setupControls();
    this.setupPointButtons();
    this.setupPopup();
    this.loadSVG();
    this.setupResizeHandler();
    this.setupHorizontalScroll();
  }

  ensureShadowMount() {
    if (!this.wrapper) return null;

    if (!this.wrapper.shadowRoot) {
      const root = this.wrapper.attachShadow({ mode: 'open' });
      root.innerHTML =
        '<style>' +
        ':host{display:block;width:100%;height:100%;}' +
        '.ehc-map-mount{width:100%;height:100%;}' +
        'svg{display:block;width:100%;height:auto;max-width:100%;}' +
        'path,rect{stroke:none;stroke-width:0;}' +
        '</style>' +
        '<div class="ehc-map-mount"></div>';
    }

    return this.wrapper.shadowRoot.querySelector('.ehc-map-mount');
  }

  fixMapPaths() {
    if (!this.svg) return;

    this.svg.querySelectorAll('path, rect').forEach(function (el) {
      var fill = el.getAttribute('fill');
      if (fill) {
        el.setAttribute('fill', fill);
        el.style.fill = fill;
      }
      el.style.stroke = 'none';
      el.style.strokeWidth = '0';
    });
  }

  loadSVG() {
    if (!this.wrapper) return;

    var url = this.wrapper.dataset.svgUrl;
    if (!url) return;

    fetch(url)
      .then(function (res) {
        return res.text();
      })
      .then(
        function (svgText) {
          var mount = this.ensureShadowMount();
          if (!mount) return;

          mount.innerHTML = svgText;
          this.svg = mount.querySelector('svg');

          if (!this.svg) return;

          this.svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
          this.fixMapPaths();
          this.readOriginalViewBox();

          if (this.pendingFocus) {
            var pf = this.pendingFocus;
            this.pendingFocus = null;
            this.focusOnPoint(pf.x, pf.y, pf.scale);
          }
        }.bind(this)
      )
      .catch(function (err) {
        console.error('Error loading SVG:', err);
      });
  }

  readOriginalViewBox() {
    if (!this.svg) return;

    var vb = null;

    if (this.svg.viewBox && this.svg.viewBox.baseVal && this.svg.viewBox.baseVal.width) {
      var b = this.svg.viewBox.baseVal;
      vb = { x: b.x, y: b.y, w: b.width, h: b.height };
    } else if (this.svg.getAttribute('viewBox')) {
      var parts = this.svg.getAttribute('viewBox').split(/[\s,]+/);
      if (parts.length === 4) {
        vb = {
          x: parseFloat(parts[0]),
          y: parseFloat(parts[1]),
          w: parseFloat(parts[2]),
          h: parseFloat(parts[3]),
        };
      }
    }

    if (!vb) {
      vb = { x: 0, y: 0, w: 845, h: 480 };
    }

    this.originalViewBox = vb;
    this.currentViewBox = { x: vb.x, y: vb.y, w: vb.w, h: vb.h };
  }

  setupResizeHandler() {
    var self = this;
    var resizeTimeout;

    window.addEventListener('resize', function () {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(function () {
        self.reset();
      }, 150);
    });
  }

  setupHorizontalScroll() {
    var section = document.getElementById(this.sectionId);
    var list = section && section.querySelector('.map-points-list');

    if (!list) return;

    list.addEventListener(
      'wheel',
      function (e) {
        if (list.scrollWidth <= list.clientWidth) return;
        e.preventDefault();
        list.scrollLeft += e.deltaY;
      },
      { passive: false }
    );
  }

  setupControls() {
    var section = document.getElementById(this.sectionId);
    var controls = section && section.querySelectorAll('.map-control-btn');
    var self = this;

    if (!controls) return;

    controls.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        var action = e.currentTarget.dataset.action;
        if (action === 'reset') self.reset();
      });
    });
  }

  setupPointButtons() {
    var section = document.getElementById(this.sectionId);
    var pointButtons = section && section.querySelectorAll('.point-button');
    var centerMarker = document.getElementById(this.sectionId + '-center-marker');
    var pointName = document.getElementById(this.sectionId + '-point-name');
    var popupTrigger = document.getElementById(this.sectionId + '-popup-trigger');
    var controls = section && section.querySelector('.map-controls');
    var self = this;

    if (!pointButtons) return;

    pointButtons.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        var button = e.currentTarget;

        if (button.classList.contains('active')) {
          self.reset();
          return;
        }

        var x = parseFloat(button.dataset.x);
        var y = parseFloat(button.dataset.y);
        var scale = parseFloat(button.dataset.scale) || 2;
        var name = button.dataset.pointName;
        var label = button.dataset.pointLabel;
        var blockId = button.dataset.blockId;
        var hasPopup = button.dataset.hasPopup === 'true';

        pointButtons.forEach(function (b) {
          b.classList.remove('active');
        });
        button.classList.add('active');

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'GAevent',
          eventCategory: 'Ingredients_Click',
          eventAction: 'click_on_ingredients',
          eventLabel: label,
        });

        self.currentBlockId = blockId;
        self.currentPointName = label;
        pointName.textContent = name;
        centerMarker.classList.add('visible');
        if (controls) controls.classList.add('visible');
        if (popupTrigger) popupTrigger.hidden = !hasPopup;

        self.focusOnPoint(x, y, scale);
      });
    });
  }

  portalPopupOverlay() {
    var overlay = document.getElementById(this.sectionId + '-popup');
    var section = document.getElementById(this.sectionId);
    if (!overlay) return;

    if (section) {
      var closeVar = section.style.getPropertyValue('--ehc-map-close');
      if (closeVar) {
        overlay.style.setProperty('--ehc-map-close', closeVar);
      }
    }

    if (overlay.parentElement !== document.body) {
      document.body.appendChild(overlay);
    }
  }

  setupPopup() {
    var overlay = document.getElementById(this.sectionId + '-popup');
    var closeBtn = document.getElementById(this.sectionId + '-popup-close');
    var trigger = document.getElementById(this.sectionId + '-popup-trigger');
    var self = this;

    if (trigger) {
      trigger.addEventListener('click', function () {
        self.openPopup(self.currentBlockId);
      });
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        self.closePopup();
      });
    }

    if (overlay) {
      overlay.addEventListener('click', function (e) {
        if (e.target === overlay) self.closePopup();
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') self.closePopup();
    });
  }

  openPopup(blockId) {
    var template = document.getElementById(this.sectionId + '-popup-tpl-' + blockId);
    if (!template) return;

    var body = document.getElementById(this.sectionId + '-popup-body');
    body.innerHTML = '';
    body.appendChild(template.cloneNode(true));
    body.firstElementChild.removeAttribute('hidden');

    var currentPointName = this.currentPointName;
    var ctaLink = body.querySelector('.map-popup-cta-link');

    if (ctaLink) {
      ctaLink.addEventListener('click', function (e) {
        var panelId =
          ctaLink.getAttribute('data-davines-panel') ||
          (ctaLink.getAttribute('href') || '').replace(/^#/, '');

        if (panelId && panelId.indexOf('col-') === 0) {
          e.preventDefault();
          self.closePopup();

          if (typeof window.davinesSetEssentialPanel === 'function') {
            window.davinesSetEssentialPanel(panelId);
          }

          window.setTimeout(function () {
            if (typeof window.davinesScrollToProductLines === 'function') {
              window.davinesScrollToProductLines(panelId);
            } else {
              var panel = document.getElementById(panelId);
              var fallback =
                (panel && panel.querySelector('.products-carousel')) ||
                document.querySelector('.davines-line-panels') ||
                document.getElementById('davines-nav-spacer');
              if (fallback) {
                fallback.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }
          }, 80);

          var pn = currentPointName || '';
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'GAevent',
            eventCategory: 'collection_' + pn.toLowerCase().replace(/\s+/g, '_'),
            eventAction: 'cta_to_tab',
            eventLabel: 'Click on discover the collection ' + ctaLink.dataset.collectionName,
          });
          return;
        }

        var pn2 = currentPointName || '';
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'GAevent',
          eventCategory: 'collection_' + pn2.toLowerCase().replace(/\s+/g, '_'),
          eventAction: 'cta_to_collection',
          eventLabel: 'Click on discover the collection ' + ctaLink.dataset.collectionName,
        });
      });
    }

    var videoOverlay = body.querySelector('.map-popup-media--video .video-play-overlay');
    if (videoOverlay) {
      var videoIframe = body.querySelector('.map-popup-media--video iframe[data-video-id]');
      var videoId = videoIframe ? videoIframe.dataset.videoId : '';

      videoOverlay.addEventListener(
        'click',
        function () {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'GAevent',
            eventCategory: 'embedded_video',
            eventAction: 'click_embedded_video',
            eventLabel: 'Youtube id ' + videoId,
          });
          videoOverlay.remove();
          if (videoIframe) {
            videoIframe.src = videoIframe.src + '&autoplay=1';
          }
        },
        { once: true }
      );
    }

    var popupOverlay = document.getElementById(this.sectionId + '-popup');
    var self = this;
    setTimeout(function () {
      popupOverlay.classList.add('active');
      popupOverlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      self.initCarousel(body);
    }, 100);
  }

  closePopup() {
    var overlay = document.getElementById(this.sectionId + '-popup');
    if (!overlay || !overlay.classList.contains('active')) return;

    overlay.classList.remove('active');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    this.destroyPopupSwiper();

    var body = document.getElementById(this.sectionId + '-popup-body');
    body.innerHTML = '';
  }

  destroyPopupSwiper() {
    if (this._popupSwiper) {
      this._popupSwiper.destroy(true, true);
      this._popupSwiper = null;
    }
  }

  syncCarouselProgress(wrap, swiper) {
    var fill = wrap.querySelector('.ehc-map-carousel-progress__fill');
    if (!fill || !swiper) return;

    var total = swiper.slides ? swiper.slides.length : 0;
    if (!total) return;

    var index = typeof swiper.activeIndex === 'number' ? swiper.activeIndex : 0;
    fill.style.width = ((index + 1) / total * 100) + '%';
  }

  initCarousel(container) {
    var wrap = container.querySelector('.map-popup-carousel-wrap');
    var swiperEl = wrap
      ? wrap.querySelector('.map-popup-carousel')
      : container.querySelector('.map-popup-carousel');
    if (!swiperEl) return;

    this.destroyPopupSwiper();

    var self = this;
    var prevEl = wrap
      ? wrap.querySelector('.swiper-button-prev')
      : swiperEl.querySelector('.swiper-button-prev');
    var nextEl = wrap
      ? wrap.querySelector('.swiper-button-next')
      : swiperEl.querySelector('.swiper-button-next');

    function mountSwiper() {
      if (typeof Swiper !== 'function') return false;

      self._popupSwiper = new Swiper(swiperEl, {
        slidesPerView: 1.1,
        spaceBetween: 12,
        grabCursor: true,
        watchOverflow: true,
        scrollbar: {
          el: swiperEl.querySelector('.swiper-scrollbar'),
          draggable: true,
        },
        navigation: {
          prevEl: prevEl,
          nextEl: nextEl,
        },
        on: {
          init: function (swiper) {
            if (wrap) self.syncCarouselProgress(wrap, swiper);
          },
          slideChange: function (swiper) {
            if (wrap) self.syncCarouselProgress(wrap, swiper);
          },
          resize: function (swiper) {
            if (wrap) self.syncCarouselProgress(wrap, swiper);
          },
        },
      });

      requestAnimationFrame(function () {
        if (!self._popupSwiper) return;
        self._popupSwiper.update();
        if (wrap) self.syncCarouselProgress(wrap, self._popupSwiper);
      });

      return true;
    }

    if (!mountSwiper()) {
      var attempts = 0;
      var timer = setInterval(function () {
        attempts += 1;
        if (mountSwiper() || attempts >= 40) clearInterval(timer);
      }, 50);
    }

    swiperEl.querySelectorAll('img').forEach(function (img) {
      if (img.complete) return;
      img.addEventListener(
        'load',
        function () {
          if (self._popupSwiper) self._popupSwiper.update();
        },
        { once: true }
      );
    });
  }

  focusOnPoint(x, y, scale) {
    if (!this.svg || !this.originalViewBox) {
      this.pendingFocus = { x: x, y: y, scale: scale };
      return;
    }

    if (window.innerWidth < 768) {
      scale = Math.min(scale * 2, 10);
    }

    var vb = this.originalViewBox;
    var newW = vb.w / scale;
    var newH = vb.h / scale;
    var newX = x - newW / 2;
    var newY = y - newH / 2;

    this.animateViewBox({ x: newX, y: newY, w: newW, h: newH });
  }

  reset() {
    this.currentBlockId = null;

    var section = document.getElementById(this.sectionId);
    if (section) {
      section.querySelectorAll('.point-button').forEach(function (btn) {
        btn.classList.remove('active');
      });
    }

    var centerMarker = document.getElementById(this.sectionId + '-center-marker');
    if (centerMarker) centerMarker.classList.remove('visible');

    var controls = section && section.querySelector('.map-controls');
    if (controls) controls.classList.remove('visible');

    var trigger = document.getElementById(this.sectionId + '-popup-trigger');
    if (trigger) trigger.hidden = true;

    if (this.originalViewBox) {
      this.animateViewBox({
        x: this.originalViewBox.x,
        y: this.originalViewBox.y,
        w: this.originalViewBox.w,
        h: this.originalViewBox.h,
      });
    }
  }

  animateViewBox(target) {
    if (!this.svg) return;

    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    var start = {
      x: this.currentViewBox.x,
      y: this.currentViewBox.y,
      w: this.currentViewBox.w,
      h: this.currentViewBox.h,
    };
    var duration = 1000;
    var startTime = performance.now();
    var self = this;

    function ease(t) {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    function step(now) {
      var elapsed = now - startTime;
      var progress = Math.min(elapsed / duration, 1);
      var t = ease(progress);

      self.currentViewBox = {
        x: start.x + (target.x - start.x) * t,
        y: start.y + (target.y - start.y) * t,
        w: start.w + (target.w - start.w) * t,
        h: start.h + (target.h - start.h) * t,
      };

      self.svg.setAttribute(
        'viewBox',
        self.currentViewBox.x +
          ' ' +
          self.currentViewBox.y +
          ' ' +
          self.currentViewBox.w +
          ' ' +
          self.currentViewBox.h
      );

      if (progress < 1) {
        self.animationId = requestAnimationFrame(step);
      } else {
        self.animationId = null;
      }
    }

    this.animationId = requestAnimationFrame(step);
  }
}
